import {
	SIGNATURE_COLUMN_ID_PREFIX,
	SIGNATURE_ROW_ID_PREFIX,
} from '@/components/UnlayerEditorWrapper/signatureDesign/constants';
import { cloneDesignSnapshot } from '@/components/UnlayerEditorWrapper/signatureDesign/cleanup';

// When a design does not already contain an html block, we still need a valid
// Unlayer html payload shape so the inserted signature behaves like native content.
export const getFallbackTemplateValues = () => ({
	displayCondition: null,
	_styleGuide: null,
	containerPadding: '10px',
	anchor: '',
	_meta: {
		htmlClassNames: 'u_content_html',
	},
	selectable: true,
	draggable: true,
	duplicatable: true,
	deletable: true,
	hideable: true,
	locked: false,
});

export const getNextCounterValue = (counters, counterName) =>
	Number.isFinite(Number(counters?.[counterName])) ? Number(counters[counterName]) + 1 : 1;

export const getFallbackSignatureRowValues = (bodyValues) => ({
	displayCondition: null,
	columns: false,
	_styleGuide: null,
	backgroundColor: bodyValues?.backgroundColor ?? '#ffffff',
	columnsBackgroundColor: '',
	backgroundImage: {
		url: '',
		fullWidth: true,
		repeat: 'no-repeat',
		size: 'custom',
		position: 'center',
	},
	padding: '0px',
	anchor: '',
	hideDesktop: false,
	_meta: {
		htmlClassNames: 'u_row',
	},
	selectable: true,
	draggable: true,
	duplicatable: true,
	deletable: true,
	hideable: true,
	locked: false,
});

export const getFallbackSignatureColumnValues = (bodyValues) => ({
	backgroundColor: bodyValues?.backgroundColor ?? '#ffffff',
	padding: '0px',
	border: {},
	borderRadius: '0px',
	_meta: {
		htmlClassNames: 'u_column',
	},
	deletable: true,
	locked: false,
});

// Build the html content block that will carry the signature markup. Reusing a
// native html template keeps internal Unlayer metadata consistent across saves.
export const buildSignatureContentBlock = ({ counters, rawSignatureHtml, template }) => {
	const htmlCounter = getNextCounterValue(counters, 'u_content_html');
	const templateValues =
		template?.values && typeof template.values === 'object'
			? cloneDesignSnapshot(template.values)
			: getFallbackTemplateValues();

	return {
		counters: {
			...counters,
			u_content_html: htmlCounter,
		},
		signatureContent: {
			id: `signature_${Date.now()}`,
			type: 'html',
			values: {
				...templateValues,
				html: rawSignatureHtml,
				_meta: {
					...(templateValues?._meta ?? {}),
					htmlID: `u_content_html_${htmlCounter}`,
					htmlClassNames: templateValues?._meta?.htmlClassNames ?? 'u_content_html',
				},
			},
		},
	};
};

// Signatures now always live in their own dedicated bottom row. That avoids
// relying on whatever content column happens to be first in a complex design.
export const appendBottomSignatureRow = ({ design, rows, signatureContent }) => {
	const bodyValues = design?.body?.values ?? {};
	const counters = design.counters ?? {};
	const nextRowCounter = getNextCounterValue(counters, 'u_row');
	const nextColumnCounter = getNextCounterValue(counters, 'u_column');
	const rowValues = getFallbackSignatureRowValues(bodyValues);
	const columnValues = getFallbackSignatureColumnValues(bodyValues);

	design.counters = {
		...counters,
		u_row: nextRowCounter,
		u_column: nextColumnCounter,
	};

	rows.push({
		id: `${SIGNATURE_ROW_ID_PREFIX}${nextRowCounter}`,
		cells: [1],
		columns: [
			{
				id: `${SIGNATURE_COLUMN_ID_PREFIX}${nextColumnCounter}`,
				contents: [signatureContent],
				values: {
					...columnValues,
					_meta: {
						...(columnValues?._meta ?? {}),
						htmlID: `u_column_${nextColumnCounter}`,
						htmlClassNames: 'u_column',
					},
				},
			},
		],
		values: {
			...rowValues,
			_meta: {
				...(rowValues?._meta ?? {}),
				htmlID: `u_row_${nextRowCounter}`,
				htmlClassNames: 'u_row',
			},
		},
	});
};
